import React, { useState, useRef, useEffect } from "react";
import {
    Fab,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    IconButton,
    Box,
    Alert,
    Paper,
    List,
    ListItem,
    Divider,
    CircularProgress,
    Tooltip,
    Chip
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import KeyboardVoiceIcon from '@mui/icons-material/KeyboardVoice';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import HearingIcon from '@mui/icons-material/Hearing';
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { t } from "i18next";
import AiSuggestionsItemCard from "../sections/aiSuggestionsItemCard";
import SuggestionMainItemInfo from "../Item/suggestionMainItemInfo";
import {
    getAuth,
    getLocalStorageMenuItem,
    setMobileBackColor,
    setMobileCardBorderColor,
    setMobileTextColor,
    setMobileTextField_BK_FONT_Border_StyleColor,
} from "../../../utility";
import helpMessageIconDark from "../../../images/Featured icon.png";
import helpMessageIconLight from "../../../images/Featured icon-1.png";

const FloatingChatButton = ({
    qrCode,
    qrName,
    sympole,
    handleFavorite,
    favorites,
    suggested,
    onActionDone,
    treeData
}) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [suggestedItems, setSuggestedItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [userQuery, setUserQuery] = useState("");
    const [responseDescription, setResponseDescription] = useState("");
    const [selectedItem, setSelectedItem] = useState(null);
    const [isItemDialogOpen, setIsItemDialogOpen] = useState(false);
    const [isNoDataDialogOpen, setIsNoDataDialogOpen] = useState(false);
    const [showWarningAlert, setShowWarningAlert] = useState(false);
    const [chatHistory, setChatHistory] = useState([]);

    // Voice recording states
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [recordingError, setRecordingError] = useState("");
    const [speechRecognition, setSpeechRecognition] = useState(null);
    const [isSpeechSupported, setIsSpeechSupported] = useState(true);
    const [interimTranscript, setInterimTranscript] = useState("");
    const [isSpeaking, setIsSpeaking] = useState(false);

    const messagesEndRef = useRef(null);
    const recordingIntervalRef = useRef(null);
    const speechSynthesisRef = useRef(null);
    const finalTranscriptRef = useRef("");

    const theme = getLocalStorageMenuItem("theme");
    const lang = getLocalStorageMenuItem("lang");
    const [searchParams] = useSearchParams();

    const isRTL = lang === "ar" || lang === "fa";
    const textDirection = isRTL ? 'rtl' : 'ltr';
    const textAlignment = isRTL ? 'right' : 'left';
    const fontFamily = isRTL ? "Almarai" : "Roboto";

    const dialogStyles = {
        backgroundColor: setMobileTextField_BK_FONT_Border_StyleColor()[0],
        color: setMobileTextField_BK_FONT_Border_StyleColor()[1],
        borderColor: setMobileTextField_BK_FONT_Border_StyleColor()[2],
    };

    const textFieldStyles = {
        direction: textDirection,
        textAlign: textAlignment,
        borderRadius: '8px',
        backgroundColor: setMobileBackColor(),
        color: setMobileTextColor(),
        borderWidth: 1,
        borderStyle: "solid",
        borderColor: dialogStyles.borderColor,
        fontFamily,
    };

    // Check speech recognition support
    useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
            setIsSpeechSupported(false);
            return;
        }

        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = getSpeechRecognitionLanguage();

        recognition.onstart = () => {
            setIsRecording(true);
            setRecordingError("");
            finalTranscriptRef.current = "";
        };

        recognition.onresult = (event) => {
            let interim = "";
            let final = "";

            for (let i = event.resultIndex; i < event.results.length; i++) {
                const transcript = event.results[i][0].transcript;
                if (event.results[i].isFinal) {
                    final += transcript;
                } else {
                    interim += transcript;
                }
            }

            // Update final transcript immediately
            if (final) {
                finalTranscriptRef.current = final;
                setUserQuery(prev => {

                    return final.trim();
                });
            }

            if (interim) {
                setInterimTranscript(interim);
            } else {
                setInterimTranscript("");
            }
        };

        recognition.onerror = (event) => {
            console.error("Speech recognition error:", event.error);

            if (event.error === 'not-allowed' || event.error === 'permission-denied') {
                setRecordingError(t("Microphone access denied. Please allow microphone permissions in your browser settings."));
            } else if (event.error !== 'no-speech' && event.error !== 'audio-capture') {
                setRecordingError(t("Speech recognition error occurred."));
            }

            setIsRecording(false);
            if (recordingIntervalRef.current) {
                clearInterval(recordingIntervalRef.current);
            }
        };

        recognition.onend = () => {
            setIsRecording(false);
            if (recordingIntervalRef.current) {
                clearInterval(recordingIntervalRef.current);
            }
            setInterimTranscript("");
        };

        setSpeechRecognition(recognition);
    }, [lang]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (recordingIntervalRef.current) {
                clearInterval(recordingIntervalRef.current);
            }
            if (speechRecognition) {
                speechRecognition.stop();
            }
            if (speechSynthesisRef.current) {
                speechSynthesisRef.current.cancel();
            }
        };
    }, [speechRecognition]);

    // Scroll to bottom of chat when new messages arrive
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [chatHistory, suggestedItems, interimTranscript]);

    const getSpeechRecognitionLanguage = () => {
        const languageMap = {
            'en': 'en-US',
            'ar': 'ar-SA',
            'fa': 'fa-IR',
            'fr': 'fr-FR',
            'es': 'es-ES',
            'de': 'de-DE'
        };
        return languageMap[lang] || 'en-US';
    };

    const getSpeechSynthesisLanguage = () => {
        const languageMap = {
            'en': 'en-US',
            'ar': 'ar-SA',
            'fa': 'fa-IR',
            'fr': 'fr-FR',
            'es': 'es-ES',
            'de': 'de-DE'
        };
        return languageMap[lang] || 'en-US';
    };

    const openDialog = () => {
        setIsDialogOpen(true);
        setChatHistory([{
            type: 'bot',
            content: t('Hi') + ` ${getLocalStorageMenuItem("mobileLoginToken") ? getLocalStorageMenuItem("customer_name") : ''}! ` + t('How can I help you with the menu today?')
        }]);
    };

    const closeDialog = () => {
        setIsDialogOpen(false);
        setSuggestedItems([]);
        setUserQuery("");
        setResponseDescription("");
        setShowWarningAlert(false);
        setChatHistory([]);
        stopRecording();
        stopSpeech();
    };

    // Voice recording functions
    const startRecording = () => {
        if (!isSpeechSupported) {
            setRecordingError(t("Speech recognition is not supported in your browser. Please use Chrome, Edge, or Safari."));
            return;
        }

        if (!speechRecognition) {
            setRecordingError(t("Speech recognition not available. Please refresh the page."));
            return;
        }

        setRecordingError("");
        setInterimTranscript("");
        finalTranscriptRef.current = "";

        try {
            speechRecognition.start();
            setRecordingTime(0);

            recordingIntervalRef.current = setInterval(() => {
                setRecordingTime(prev => prev + 1);
            }, 1000);
        } catch (error) {
            console.error("Error starting speech recognition:", error);
            setRecordingError(t("Failed to start voice recording. Please try again."));
        }
    };

    const stopRecording = () => {
        if (speechRecognition && isRecording) {
            speechRecognition.stop();
        }

        if (recordingIntervalRef.current) {
            clearInterval(recordingIntervalRef.current);
        }

        setIsRecording(false);
        setInterimTranscript("");
    };

    const speakText = (text) => {
        if (!window.speechSynthesis) {
            setRecordingError(t("Text-to-speech is not supported."));
            return;
        }

        stopSpeech();

        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = getSpeechSynthesisLanguage();
        utterance.rate = 0.9;
        utterance.pitch = 1;
        utterance.volume = 0.8;

        utterance.onstart = () => setIsSpeaking(true);
        utterance.onend = () => setIsSpeaking(false);
        utterance.onerror = () => {
            setIsSpeaking(false);
            setRecordingError(t("Error playing audio. Please check your audio settings."));
        };

        speechSynthesisRef.current = utterance;
        window.speechSynthesis.speak(utterance);
    };

    const stopSpeech = () => {
        if (window.speechSynthesis) {
            window.speechSynthesis.cancel();
        }
        setIsSpeaking(false);
    };

    const formatRecordingTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const fetchAISuggestions = async (command = null) => {
        const query = String(command || userQuery || "");

        if (!query?.trim()) return;

        setChatHistory(prev => [...prev, {
            type: 'user',
            content: query
        }]);

        setSuggestedItems([]);
        setResponseDescription("");
        setIsLoading(true);
        setShowWarningAlert(false);

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API}/menus/${qrCode}/recommend-items-with-ai-2`,
                { prompt: query + " " + JSON.stringify(treeData?.details) },
                getAuth()
            );

            const items = response?.data?.data?.items;
            const description = response?.data?.data?.description;

            setResponseDescription(description);

            if (items?.length > 0 || description) {
                if (items?.length > 0) {
                    setSuggestedItems(items);
                    setShowWarningAlert(true);
                }

                setChatHistory(prev => [...prev, {
                    type: 'bot',
                    content: description || t("Here are some suggestions based on your query:")
                }]);
            } else {
                setIsNoDataDialogOpen(true);
                setChatHistory(prev => [...prev, {
                    type: 'bot',
                    content: t("I couldn't find any items matching your request.")
                }]);
            }
        } catch (error) {
            console.error("Error fetching AI suggestions:", error);
            setChatHistory(prev => [...prev, {
                type: 'bot',
                content: t("Sorry, I encountered an error.")
            }]);
        } finally {
            setIsLoading(false);
            setUserQuery("");
        }
    };

    const openItemDetails = (item) => {
        setSelectedItem(item);
        setIsItemDialogOpen(true);
    };

    const closeItemDetails = () => {
        setIsItemDialogOpen(false);
        setSelectedItem(null);
    };

    const closeNoDataDialog = () => setIsNoDataDialogOpen(false);
    const closeWarningAlert = () => setShowWarningAlert(false);

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            fetchAISuggestions(null);
        }
    };

    // Quick voice commands suggestions
    const quickCommands = [
        t("Show me vegetarian options"),
        t("What are your specialties?"),
        t("I want something spicy"),
        t("Show me desserts"),
        t("What do you recommend?"),
        t("Show me drinks")
    ];

    const handleQuickCommand = async (command) => {
        setUserQuery(command);
        setTimeout(() => {
            fetchAISuggestions(command);
        }, 500);
    };

    return (
        <>
            {/* Floating Action Button */}
            <Fab
                color="primary"
                aria-label="AI assistant"
                onClick={openDialog}
                style={{
                    position: 'fixed',
                    bottom: !treeData?.details?.order_ability && !treeData?.details?.whatsapp ? '10px' : '90px',
                    right: '16px',
                    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2)',
                    backgroundColor: setMobileBackColor(),
                }}
            >
                <img
                    src={theme === "dark" ? helpMessageIconDark : helpMessageIconLight}
                    alt="AI Assistant"
                    style={{ width: "60px", height: "60px" }}
                />
            </Fab>

            {/* No Data Found Dialog */}
            <Dialog
                open={isNoDataDialogOpen}
                onClose={closeNoDataDialog}
                maxWidth="xs"
                PaperProps={{
                    style: {
                        borderRadius: 15,
                        padding: "20px",
                        backgroundColor: dialogStyles.backgroundColor
                    },
                }}
            >
                <DialogTitle sx={{ textAlign: "center", paddingBottom: 0 }}>
                    <Typography variant="h5" fontWeight="bold" color={dialogStyles.color}>
                        {t("No Matching Data")}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: "flex", justifyContent: "center", padding: "20px 10px" }}>
                        <Typography variant="body1" textAlign="center" color={dialogStyles.color}>
                            {t("No data matching your search.")}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "center" }}>
                    <Button
                        onClick={closeNoDataDialog}
                        sx={{
                            backgroundColor: dialogStyles.color,
                            color: dialogStyles.backgroundColor,
                            padding: "10px 20px",
                            borderRadius: "30px",
                            "&:hover": {
                                backgroundColor: "primary.dark",
                            },
                        }}
                    >
                        {t("Close")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* AI Warning Alert Dialog */}
            <Dialog
                open={showWarningAlert}
                onClose={closeWarningAlert}
                maxWidth="sm"
                PaperProps={{
                    style: {
                        borderRadius: 15,
                        padding: "20px",
                        backgroundColor: dialogStyles.backgroundColor,
                        border: `1px solid ${theme === 'dark' ? '#ff9800' : '#f57c00'}`,
                    },
                }}
            >
                <DialogTitle sx={{ textAlign: "center", paddingBottom: 0 }}>
                    <Typography variant="h6" fontWeight="bold" color={theme === 'dark' ? '#ff9800' : '#f57c00'}>
                        {t("Important Note")}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: "flex", justifyContent: "center", padding: "10px" }}>
                        <Typography variant="body1" textAlign="center" color={dialogStyles.color}>
                            {t("These items are suggested by AI. Please take care if you have any allergies or special requirements.")}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "center" }}>
                    <Button
                        onClick={closeWarningAlert}
                        sx={{
                            backgroundColor: theme === 'dark' ? '#ff9800' : '#f57c00',
                            color: '#fff',
                            padding: "8px 16px",
                            borderRadius: "30px",
                            "&:hover": {
                                backgroundColor: theme === 'dark' ? '#f57c00' : '#e65100',
                            },
                        }}
                    >
                        {t("I Understand")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Main AI Assistant Dialog */}
            <Dialog
                className="alignByLang"
                open={isDialogOpen}
                onClose={closeDialog}
                fullWidth
                maxWidth="sm"
                fullScreen
                PaperProps={{
                    style: {
                        borderRadius: 0,
                        ...dialogStyles,
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                    },
                }}
            >
                <DialogTitle style={{
                    padding: '16px',
                    borderBottom: `1px solid ${dialogStyles.borderColor}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Typography className="head-font" variant="h6" style={{ fontWeight: 'bold', color: setMobileTextColor() }}>
                        {t("Menu Assistant")}
                    </Typography>
                    <IconButton onClick={closeDialog}>
                        <CloseIcon style={{ color: setMobileTextColor() }} />
                    </IconButton>
                </DialogTitle>

                <DialogContent style={{
                    flex: 1,
                    padding: '0',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    {/* Chat History */}
                    <div className="alignByLang" style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '16px',

                    }}>
                        <List>
                            {/* Quick Commands */}
                            {chatHistory.length === 1 && (
                                <ListItem className="alignByLang" >
                                    <Box sx={{ width: '100%' }}>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                mb: 1,
                                                color: setMobileTextColor(),
                                                fontFamily,
                                                fontWeight: 'bold',
                                            }}
                                        >
                                            {t("Try asking:")}
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                            {quickCommands.map((command, index) => (
                                                <Chip
                                                    key={index}
                                                    label={command}
                                                    onClick={() => handleQuickCommand(command)}
                                                    size="small"
                                                    sx={{
                                                        fontFamily,
                                                        cursor: 'pointer',
                                                        backgroundColor: setMobileCardBorderColor(),
                                                        color: setMobileBackColor(),
                                                        '&:hover': {
                                                            backgroundColor: setMobileTextColor(),
                                                            color: setMobileBackColor(),
                                                        }
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    </Box>
                                </ListItem>
                            )}

                            {chatHistory.map((message, index) => (
                                <React.Fragment key={index}>
                                    <ListItem
                                        style={{
                                            justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start',
                                            padding: '8px 0',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, maxWidth: '80%' }}>
                                            {message.type === 'bot' && (
                                                <Tooltip title={isSpeaking ? t("Stop speaking") : t("Listen to response")}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => isSpeaking ? stopSpeech() : speakText(message.content)}
                                                        sx={{
                                                            color: isSpeaking ? '#f44336' : setMobileTextColor(),
                                                            mt: '4px'
                                                        }}
                                                    >
                                                        {isSpeaking ? <VolumeUpIcon /> : <HearingIcon />}
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            <Paper
                                                elevation={0}
                                                style={{
                                                    padding: '12px 16px',
                                                    flex: 1,
                                                    backgroundColor: message.type === 'user'
                                                        ? setMobileBackColor()
                                                        : setMobileCardBorderColor(),
                                                    color: message.type === 'user'
                                                        ? setMobileTextColor()
                                                        : setMobileBackColor(),
                                                    borderRadius: message.type === 'user'
                                                        ? !isRTL
                                                            ? '18px 18px 0 18px'
                                                            : '18px 18px 18px 0'
                                                        : !isRTL
                                                            ? '18px 18px 18px 0'
                                                            : '18px 18px 0 18px',
                                                }}
                                            >
                                                <Typography style={{ fontFamily }}>
                                                    {message.content}
                                                </Typography>
                                            </Paper>
                                        </Box>
                                    </ListItem>
                                    {index < chatHistory.length - 1 && <Divider />}
                                </React.Fragment>
                            ))}

                            {/* Interim transcript during recording */}
                            {interimTranscript && (
                                <ListItem style={{ justifyContent: 'flex-start', padding: '8px 0' }}>
                                    <Paper
                                        elevation={0}
                                        style={{
                                            padding: '12px 16px',
                                            maxWidth: '80%',
                                            backgroundColor: setMobileCardBorderColor(),
                                            color: setMobileBackColor(),
                                            border: `2px dashed ${theme === 'dark' ? '#ff9800' : '#f57c00'}`,
                                            borderRadius: !isRTL ? '18px 18px 18px 0' : '18px 18px 0 18px',
                                        }}
                                    >
                                        <Typography style={{ fontFamily, fontStyle: 'italic', opacity: 0.8 }}>
                                            {interimTranscript}
                                        </Typography>
                                    </Paper>
                                </ListItem>
                            )}

                            {/* Loading indicator */}
                            {isLoading && (
                                <ListItem style={{ justifyContent: 'flex-start', padding: '8px 0' }}>
                                    <Paper
                                        elevation={0}
                                        style={{
                                            padding: '12px 16px',
                                            maxWidth: '80%',
                                            backgroundColor: setMobileCardBorderColor(),
                                            color: setMobileBackColor(),
                                            borderRadius: !isRTL ? '18px 18px 18px 0' : '18px 18px 0 18px',
                                        }}
                                    >
                                        <Typography style={{ fontFamily }}>
                                            {t("Thinking...")}
                                        </Typography>
                                    </Paper>
                                </ListItem>
                            )}

                            {/* Suggested Items */}
                            {suggestedItems?.length > 0 && (
                                <>
                                    <Divider />
                                    <ListItem style={{
                                        justifyContent: 'flex-start',
                                        padding: '8px 0',
                                        flexDirection: 'column',
                                        alignItems: 'flex-start'
                                    }}>
                                        <Typography
                                            variant="subtitle2"
                                            style={{
                                                marginBottom: '8px',
                                                color: setMobileTextColor(),
                                                fontFamily,
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            {t("Suggested items")}:
                                        </Typography>
                                        <div style={{
                                            width: '100%',
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                                            gap: '12px'
                                        }}>
                                            {suggestedItems.map((item) => (
                                                <AiSuggestionsItemCard
                                                    key={item.id}
                                                    item={item}
                                                    in_root={false}
                                                    qrCode={qrCode}
                                                    qrName={qrName}
                                                    sympole={sympole || ""}
                                                    handleFavorite={handleFavorite}
                                                    favorites={favorites}
                                                    suggested={suggested}
                                                    handleOpenItemDialog={openItemDetails}
                                                    style={{ margin: '4px 0' }}
                                                />
                                            ))}
                                        </div>
                                    </ListItem>
                                </>
                            )}
                            <div ref={messagesEndRef} />
                        </List>
                    </div>

                    {/* Input Area - Fixed at bottom */}
                    <div style={{
                        padding: '16px',
                        borderTop: `1px solid ${dialogStyles.borderColor}`,
                        backgroundColor: dialogStyles.backgroundColor
                    }}>
                        {/* Recording Indicator */}
                        {isRecording && (
                            <Box
                                display="flex"
                                alignItems="center"
                                gap={1}
                                sx={{
                                    mb: 1,
                                    p: 1,
                                    borderRadius: 2,
                                    backgroundColor: theme === 'dark' ? 'rgba(244, 67, 54, 0.1)' : 'rgba(244, 67, 54, 0.05)',
                                    border: `1px solid ${setMobileCardBorderColor()}`
                                }}
                            >
                                <CircularProgress size={20} color="error" />
                                <Typography
                                    variant="body2"
                                    style={{
                                        color: setMobileTextColor(),
                                        fontFamily,
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {t("Listening...")} • {formatRecordingTime(recordingTime)}
                                </Typography>
                                <IconButton
                                    size="small"
                                    onClick={stopRecording}
                                    sx={{
                                        ml: 'auto',
                                        color: '#f44336'
                                    }}
                                >
                                    <StopIcon />
                                </IconButton>
                            </Box>
                        )}

                        {/* Browser Support Warning */}
                        {!isSpeechSupported && (
                            <Alert
                                severity="warning"
                                sx={{ mb: 1 }}
                                icon={<MicIcon />}
                            >
                                {t("Voice input is not supported in this browser.")}
                            </Alert>
                        )}

                        {/* Error Message */}
                        {recordingError && (
                            <Alert
                                severity="error"
                                sx={{ mb: 1 }}
                                onClose={() => setRecordingError("")}
                            >
                                {recordingError}
                            </Alert>
                        )}

                        <Box display="flex" alignItems="center" gap={1}>
                            {/* Voice Record Button */}
                            <Tooltip title={isRecording ? t("Stop recording") : t("Voice input")}>
                                <IconButton
                                    onClick={isRecording ? stopRecording : startRecording}
                                    disabled={!isSpeechSupported}
                                    style={{
                                        backgroundColor: isRecording
                                            ? '#f44336'
                                            : !isSpeechSupported
                                                ? (theme === 'dark' ? '#424242' : '#e0e0e0')
                                                : (theme === 'dark' ? '#1976d2' : '#bbdefb'),
                                        color: isRecording
                                            ? '#fff'
                                            : !isSpeechSupported
                                                ? setMobileTextColor()
                                                : (theme === 'dark' ? '#fff' : '#0d47a1'),
                                        animation: isRecording ? 'pulse 1.5s infinite' : 'none',
                                        opacity: !isSpeechSupported ? 0.5 : 1,
                                    }}
                                >
                                    {isRecording ? <MicIcon /> : <KeyboardVoiceIcon />}
                                </IconButton>
                            </Tooltip>

                            {/* Text Input */}
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder={isSpeechSupported
                                    ? t("Ask about menu items or use voice...")
                                    : t("Ask about menu items...")
                                }
                                value={userQuery}
                                onChange={(e) => setUserQuery(e.target.value)}
                                onKeyPress={handleKeyPress}
                                multiline
                                maxRows={4}
                                InputProps={{
                                    style: {
                                        ...textFieldStyles,
                                        borderRadius: '24px',
                                        paddingRight: '12px'
                                    },
                                }}
                            />

                            {/* Send Button */}
                            <Tooltip title={t("Send message")}>
                                <IconButton
                                    color="primary"
                                    onClick={() => fetchAISuggestions(null)}
                                    disabled={!userQuery.trim() || isLoading}
                                    style={{
                                        backgroundColor: (userQuery.trim() && !isLoading)
                                            ? (theme === 'dark' ? '#1976d2' : '#bbdefb')
                                            : (theme === 'dark' ? '#424242' : '#e0e0e0'),
                                        transform: isRTL ? 'scaleX(-1)' : 'none',
                                    }}
                                >
                                    {isLoading ? <CircularProgress size={24} /> : <SendIcon />}
                                </IconButton>
                            </Tooltip>
                        </Box>

                        <Typography
                            variant="caption"
                            display="block"
                            style={{
                                marginTop: '4px',
                                color: setMobileTextColor(),
                                opacity: 0.7,
                                textAlign: 'center',
                                fontFamily
                            }}
                        >
                            {isSpeechSupported
                                ? t("Type your question or tap the microphone to speak")
                                : t("Type your question to get menu suggestions")
                            }
                        </Typography>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Item Details Dialog */}
            {selectedItem && (
                <Dialog
                    open={isItemDialogOpen}
                    onClose={closeItemDetails}
                    fullWidth
                    fullScreen
                    maxWidth="sm"
                    PaperProps={{
                        style: {
                            height: "100%",
                            ...dialogStyles
                        },
                    }}
                >
                    <SuggestionMainItemInfo
                        item={selectedItem}
                        sympoleData={sympole}
                        handleFavorite={handleFavorite}
                        favorites={favorites}
                        tableQr={qrCode}
                        tableName={qrName}
                        handleCloseItemDialog={closeItemDetails}
                        onActionDone={onActionDone}
                        treeData={treeData}
                    />
                </Dialog>
            )}
        </>
    );
};

export default FloatingChatButton;